import { useBlockNumber } from 'wagmi';
import Auctions from './Auctions';
import useAuctions from '@/application/read/auctions/useAuctions';
import useVaults from '@/application/read/vaults/useVaults';
import { useMemo, useState } from 'react';
import { PREMIUM_DURATION, Zero, getChainConstant } from 'nftx.js';
import usePremiumPaids from '@/application/read/auctions/usePremiumPaids';
import AuctionContainer from './AuctionContainer';
import { useNetwork } from '@nftx/react';

export interface AuctionsContainerProps {}

const AuctionsContainer = ({}: AuctionsContainerProps) => {
  const network = useNetwork();
  const [sortDir, setSortDir] = useState<'newest' | 'oldest'>('newest');
  const [vaultId, setVaultId] = useState('');
  const { data: blockNumber } = useBlockNumber({ watch: true });
  const { data: allAssets, isLoading } = useAuctions();
  const { data: allVaults } = useVaults();
  const { data: premiumPaids } = usePremiumPaids({ vaultId });

  const vaults = useMemo(() => {
    const vaultIds = allAssets?.flatMap((asset) => asset.vaultIds) ?? [];
    return allVaults?.filter((vault) => vaultIds.includes(vault.vaultId));
  }, [allVaults, allAssets]);

  const selectedVault = useMemo(() => {
    if (!vaultId) {
      return;
    }
    return vaults?.find((vault) => vault.vaultId === vaultId);
  }, [vaultId, vaults]);
  const handleVaultSelect = (vaultId: string) => setVaultId(vaultId);

  const totalSales = premiumPaids?.length;
  const totalEthGain = useMemo(() => {
    return premiumPaids?.reduce((total, x) => total + x.amount, Zero);
  }, [premiumPaids]);

  const assets = useMemo(() => {
    let assets = allAssets;

    if (!assets) {
      return assets;
    }

    // Filter by vault
    if (vaultId) {
      assets = assets.filter((asset) => asset.vaultIds.includes(vaultId));
    }

    // Filter by "no longer premium"
    const now = Math.floor(Date.now() / 1000);
    const premiumStart = now - getChainConstant(PREMIUM_DURATION, network);

    assets = assets.filter((asset) => {
      const dateAdded = asset.dateAdded ?? 0;
      const auctionStart = dateAdded < premiumStart ? 0 : dateAdded;
      const auctionEnd =
        auctionStart + getChainConstant(PREMIUM_DURATION, network);
      const hasEnded = auctionEnd < now - 60;
      return !hasEnded;
    });

    assets = assets.sort((a, b) => {
      const av = a.dateAdded ?? 0;
      const bv = b.dateAdded ?? 0;
      if (av < bv) {
        return sortDir === 'oldest' ? -1 : 1;
      }
      if (av > bv) {
        return sortDir === 'newest' ? -1 : 1;
      }
      return 0;
    });

    return assets;
  }, [allAssets, vaultId, sortDir, network]);

  return (
    <Auctions
      items={assets}
      isLoading={isLoading}
      blockNumber={blockNumber}
      vaults={vaults}
      selectedVault={selectedVault}
      onVaultSelect={handleVaultSelect}
      totalEthGain={totalEthGain}
      totalSales={totalSales}
      sortDir={sortDir}
      setSortDir={setSortDir}
    >
      {assets?.map((asset) => (
        <AuctionContainer key={asset.id} {...asset} />
      ))}
    </Auctions>
  );
};

export default AuctionsContainer;
